import React, { useMemo } from "react";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { TableLoader, TableNotFound } from "src/Utils/TableLoader";
import type { ITicketListItem } from "../type/ticketScanType";
import TicketListTableRow from "./TicketListTableRow";

interface Props {
	isLoading: boolean;
	data: ITicketListItem[];
	paginationSkip: number | string;
	onScanClick: (ticket: ITicketListItem) => void;
	onEditClick?: (ticket: ITicketListItem) => void;
	onDeleteClick?: (ticket: ITicketListItem) => void;
}

const TicketListTable: React.FC<Props> = ({
	isLoading,
	data,
	paginationSkip,
	onScanClick,
	onEditClick,
	onDeleteClick,
}) => {
	const totalAmount = useMemo(() => {
		if (!data || !Array.isArray(data)) return 0;
		return data.reduce((acc, item) => {
			const val = parseFloat(String(item?.total_amount || 0));
			return acc + (isNaN(val) ? 0 : val);
		}, 0);
	}, [data]);

	const totalQuantity = useMemo(() => {
		if (!data || !Array.isArray(data)) return 0;
		return data.reduce((acc, item) => {
			const val = Number(item?.quantity) || 0;
			return acc + val;
		}, 0);
	}, [data]);

	return (
		<TableContainer>
			<Table>
				<TableHead>
					<TableRow sx={{ bgcolor: "#f8fafc" }}>
						<TableCell sx={{ fontWeight: "bold", width: 60 }}>SL.</TableCell>
						<TableCell sx={{ fontWeight: "bold" }}>Ticket Info</TableCell>
						<TableCell sx={{ fontWeight: "bold" }}>Visitor Info</TableCell>
						<TableCell sx={{ fontWeight: "bold" }}>Quantity & Pricing</TableCell>
						<TableCell sx={{ fontWeight: "bold" }}>Payment Details</TableCell>
						<TableCell sx={{ fontWeight: "bold" }}>Pass Entry</TableCell>
						<TableCell sx={{ fontWeight: "bold" }} align="center">
							Action
						</TableCell>
					</TableRow>
				</TableHead>

				{isLoading ? (
					<TableLoader />
				) : data && data.length > 0 ? (
					<>
						<TableBody>
							{data.map((ticket, index) => (
								<TicketListTableRow
									key={ticket.id || index}
									ticket={ticket}
									index={index}
									paginationSkip={paginationSkip}
									onScanClick={onScanClick}
									onEditClick={onEditClick}
									onDeleteClick={onDeleteClick}
								/>
							))}
						</TableBody>
						<TableFooter>
							<TableRow
								sx={{
									bgcolor: "#f8fafc",
									borderTop: "2px solid #e2e8f0",
									"& .MuiTableCell-root": {
										py: 1.5,
									},
								}}
							>
								<TableCell
									colSpan={3}
									align="right"
									sx={{
										fontWeight: 700,
										fontSize: "0.875rem",
										color: "text.primary",
									}}
								>
									Total:
								</TableCell>
								<TableCell>
									<Typography
										variant="body2"
										fontWeight={800}
										color="primary.main"
										sx={{ fontSize: "0.95rem" }}
									>
										৳
										{totalAmount.toLocaleString(undefined, {
											minimumFractionDigits: 2,
											maximumFractionDigits: 2,
										})}
									</Typography>
									{totalQuantity > 0 && (
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{ fontWeight: 600, display: "block" }}
										>
											Total Qty: {totalQuantity}
										</Typography>
									)}
								</TableCell>
								<TableCell colSpan={3} />
							</TableRow>
						</TableFooter>
					</>
				) : (
					<TableNotFound />
				)}
			</Table>
		</TableContainer>
	);
};

export default TicketListTable;

